import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Save, RefreshCw, Phone, Mail, MapPin, Clock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const ContactManager = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await apiService.getSetting(token, 'business_info');
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateSetting(token, 'business_info', settings);
      toast.success('Business information saved!');
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const updateHours = (day, value) => {
    setSettings({
      ...settings,
      business_hours: { ...settings.business_hours, [day]: value }
    });
  };

  const updateSocialLink = (platform, value) => {
    setSettings({
      ...settings,
      social_links: { ...settings.social_links, [platform]: value }
    });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="contact-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Contact & Business Info</h1>
          <p className="text-slate-500">Manage your business contact information</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>Your business name and tagline</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Business Name</Label>
              <Input
                value={settings?.name || ''}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                placeholder="Your Business Name"
              />
            </div>
            <div className="space-y-2">
              <Label>Tagline</Label>
              <Input
                value={settings?.tagline || ''}
                onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                placeholder="Your business tagline"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Phone className="w-5 h-5" /> Contact Details</CardTitle>
          <CardDescription>Phone, WhatsApp, and email</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                value={settings?.phone || ''}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                placeholder="085128 53955"
              />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp Number (with country code)</Label>
              <Input
                value={settings?.whatsapp || ''}
                onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })}
                placeholder="918512853955"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={settings?.email || ''}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                placeholder="contact@example.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5" /> Address & Location</CardTitle>
          <CardDescription>Physical address and Google Maps</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Full Address</Label>
            <Textarea
              value={settings?.address || ''}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              placeholder="Your full business address"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label>Google Maps Embed URL</Label>
            <Input
              value={settings?.google_maps_embed || ''}
              onChange={(e) => setSettings({ ...settings, google_maps_embed: e.target.value })}
              placeholder="https://www.google.com/maps/embed?pb=..."
            />
            <p className="text-xs text-slate-500">Paste the embed URL from Google Maps (Share &gt; Embed a map)</p>
          </div>
          <div className="space-y-2">
            <Label>Google Maps Link (for directions)</Label>
            <Input
              value={settings?.google_maps_link || ''}
              onChange={(e) => setSettings({ ...settings, google_maps_link: e.target.value })}
              placeholder="https://www.google.com/maps/..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Business Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Business Hours</CardTitle>
          <CardDescription>When your business is open</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {daysOfWeek.map((day) => (
              <div key={day} className="flex items-center gap-3">
                <Label className="w-24 capitalize">{day}</Label>
                <Input
                  value={settings?.business_hours?.[day] || ''}
                  onChange={(e) => updateHours(day, e.target.value)}
                  placeholder="8:00 AM - 8:00 PM or Closed"
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Social Links */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ExternalLink className="w-5 h-5" /> Social Media Links</CardTitle>
          <CardDescription>Your social media profiles</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Facebook</Label>
              <Input
                value={settings?.social_links?.facebook || ''}
                onChange={(e) => updateSocialLink('facebook', e.target.value)}
                placeholder="https://facebook.com/..."
              />
            </div>
            <div className="space-y-2">
              <Label>Instagram</Label>
              <Input
                value={settings?.social_links?.instagram || ''}
                onChange={(e) => updateSocialLink('instagram', e.target.value)}
                placeholder="https://instagram.com/..."
              />
            </div>
            <div className="space-y-2">
              <Label>YouTube</Label>
              <Input
                value={settings?.social_links?.youtube || ''}
                onChange={(e) => updateSocialLink('youtube', e.target.value)}
                placeholder="https://youtube.com/..."
              />
            </div>
            <div className="space-y-2">
              <Label>Google Business Profile</Label>
              <Input
                value={settings?.social_links?.google_business || ''}
                onChange={(e) => updateSocialLink('google_business', e.target.value)}
                placeholder="https://business.google.com/..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Google Rating */}
      <Card>
        <CardHeader>
          <CardTitle>Google Rating Display</CardTitle>
          <CardDescription>Rating shown on website</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Google Rating (1-5)</Label>
              <Input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={settings?.google_rating || 5}
                onChange={(e) => setSettings({ ...settings, google_rating: parseFloat(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Total Reviews Count</Label>
              <Input
                type="number"
                value={settings?.total_reviews || 0}
                onChange={(e) => setSettings({ ...settings, total_reviews: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-sky-500 hover:bg-sky-600">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

export default ContactManager;
